import React, { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogPortal,
  DialogOverlay,
} from "../ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Upload, Trash2, ImageIcon, AlertTriangle, Loader2 } from "lucide-react";
import { auth } from "../../config/firebase";
import { cn } from "../../lib/utils";
import { getTeleviewImages, uploadTeleviewImage, deleteTeleviewImage } from "../../services/api";

export default function ImageUploadDialog({ masjidId, open, onOpenChange, onImagesChanged, primaryColor }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadNonce, setUploadNonce] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { type: 'single' | 'all', storagePath?: string }
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const imageObjectUrlsRef = useRef([]);

  // Use provided primaryColor or fall back to CSS variable
  const accentColor = primaryColor || "hsl(var(--primary))";

  const revokeImageObjectUrls = () => {
    imageObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    imageObjectUrlsRef.current = [];
  };

  // Load images when dialog opens
  useEffect(() => {
    if (!masjidId || !open) return;

    const fetchImageWithAuth = async (downloadUrl) => {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken(false) : null;
      const res = await fetch(downloadUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      imageObjectUrlsRef.current.push(objectUrl);
      return objectUrl;
    };

    const loadImages = async () => {
      setLoading(true);
      try {
        const resp = await getTeleviewImages(masjidId);
        if (resp?.ok && Array.isArray(resp.images)) {
          revokeImageObjectUrls();
          const imagesList = await Promise.all(
            resp.images.map(async (img) => {
              const base = {
                id: img.storagePath || img.url,
                name: img.name,
                storagePath: img.storagePath,
                uploadedAt: img.uploadedAt ? new Date(img.uploadedAt) : null,
              };
              if (!img.url) return { ...base, url: "" };
              try {
                const objectUrl = await fetchImageWithAuth(img.url);
                return { ...base, url: objectUrl };
              } catch (e) {
                console.error("Error fetching image with auth", e);
                return { ...base, url: "" };
              }
            })
          );
          // Sort by upload date (newest first)
          imagesList.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));
          setImages(imagesList);
        } else {
          setImages([]);
        }
      } catch (error) {
        console.error("Error loading images:", error);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    loadImages();
    return () => {
      revokeImageObjectUrls();
    };
  }, [masjidId, open, uploadNonce]);

  // Reset confirm state when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmDelete(null);
    }
  }, [open]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    console.log(`[ImageUploadDialog] Starting upload of ${files.length} file(s)`);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`[ImageUploadDialog] Uploading file ${i + 1}/${files.length}: ${file.name} (${file.size} bytes)`);
        await uploadTeleviewImage(masjidId, file);
        console.log(`[ImageUploadDialog] Successfully uploaded ${file.name}`);
      }
      // trigger refresh
      console.log(`[ImageUploadDialog] All uploads complete, refreshing image list`);
      setUploadNonce(Date.now());
      onImagesChanged?.();
    } catch (error) {
      console.error("[ImageUploadDialog] Upload error:", error);
      const errorMsg = error?.response?.data?.error || error?.message || "Failed to upload images. Please try again.";
      alert(`Upload failed: ${errorMsg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    await uploadFiles(files);
  };

  const handleDeleteImage = async (storagePath) => {
    setDeleting(true);
    try {
      await deleteTeleviewImage(masjidId, storagePath);
      setConfirmDelete(null);
      setUploadNonce(Date.now());
      onImagesChanged?.();
    } catch (error) {
      console.error("Error deleting image:", error);
      const errorMsg = error?.message || "Failed to delete image. Please try again.";
      alert(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const handleClearAll = async () => {
    setDeleting(true);
    try {
      const deletePromises = images.map((img) => deleteTeleviewImage(masjidId, img.storagePath));
      await Promise.all(deletePromises);
      setImages([]);
      setConfirmDelete(null);
      setUploadNonce(Date.now());
      onImagesChanged?.();
    } catch (error) {
      console.error("Error clearing all images:", error);
      const errorMsg = error?.message || "Failed to delete all images. Please try again.";
      alert(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const imageFiles = Array.from(files).filter(file =>
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) {
      alert("Please drop image files only.");
      return;
    }

    await uploadFiles(imageFiles);
  };

  const getImageToDelete = () => {
    if (confirmDelete?.type === 'single' && confirmDelete.storagePath) {
      return images.find(img => img.storagePath === confirmDelete.storagePath);
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[60] bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60]",
            "flex flex-col w-[70vw] min-w-[320px] h-[85vh] max-h-[850px]",
            "bg-background border border-border/50 rounded-2xl shadow-2xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-border/50">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${accentColor}20` }}>
                <ImageIcon className="h-6 w-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Slideshow Images</h2>
                <p className="text-sm text-muted-foreground">
                  {images.length} {images.length === 1 ? 'image' : 'images'}
                </p>
              </div>
            </div>
            <DialogPrimitive.Close className="p-2.5 rounded-lg hover:bg-muted transition-colors">
              <X className="h-6 w-6" />
            </DialogPrimitive.Close>
          </div>

          {/* Delete Confirmation Modal Overlay */}
          {confirmDelete && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-2xl">
              <div className="bg-background border border-border rounded-2xl shadow-2xl p-6 mx-6 max-w-md w-full animate-in fade-in zoom-in-95 duration-200">
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 rounded-full bg-destructive/10 mb-4">
                    <AlertTriangle className="h-8 w-8 text-destructive" />
                  </div>
                  <h3 className="text-xl font-semibold">
                    {confirmDelete.type === 'all' ? 'Delete All Images?' : 'Delete Image?'}
                  </h3>
                  <p className="text-base text-muted-foreground mt-2">
                    {confirmDelete.type === 'all'
                      ? `This will permanently delete all ${images.length} images. This action cannot be undone.`
                      : `Delete "${getImageToDelete()?.name || 'this image'}"? This action cannot be undone.`
                    }
                  </p>
                  <div className="flex items-center gap-3 mt-6 w-full">
                    <Button
                      variant="outline"
                      className="flex-1"
                      disabled={deleting}
                      onClick={() => setConfirmDelete(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      disabled={deleting}
                      onClick={() => {
                        if (confirmDelete.type === 'all') {
                          handleClearAll();
                        } else if (confirmDelete.storagePath) {
                          handleDeleteImage(confirmDelete.storagePath);
                        }
                      }}
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : 'Delete'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upload Area */}
          <div
            className="mx-6 mt-5"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            <div
              onClick={() => !uploading && fileInputRef.current?.click()}
              className={cn(
                "relative rounded-xl border-2 border-dashed p-8 transition-all cursor-pointer",
                "flex flex-col items-center justify-center gap-3",
                !isDragging && "border-muted-foreground/20 hover:bg-muted/50"
              )}
              style={{
                borderColor: isDragging ? accentColor : undefined,
                backgroundColor: isDragging ? `${accentColor}08` : undefined,
                transform: isDragging ? "scale(1.01)" : undefined,
              }}
            >
              <div
                className="p-4 rounded-full transition-colors"
                style={{
                  backgroundColor: isDragging ? `${accentColor}20` : "hsl(var(--muted))",
                }}
              >
                <Upload
                  className="h-8 w-8 transition-colors"
                  style={{ color: isDragging ? accentColor : "hsl(var(--muted-foreground))" }}
                />
              </div>
              <div className="text-center">
                <p className="text-base font-medium">
                  {uploading ? 'Uploading...' : isDragging ? 'Drop images here' : 'Drop images or click to browse'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Supports JPG, PNG, GIF, WebP
                </p>
              </div>
            </div>
          </div>

          {/* Images Grid */}
          <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-16">
                <Loader2 className="h-12 w-12 animate-spin mb-4" style={{ color: accentColor }} />
                <p className="text-lg text-muted-foreground font-medium">Loading images...</p>
              </div>
            ) : uploading ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-16">
                <Loader2 className="h-12 w-12 animate-spin mb-4" style={{ color: accentColor }} />
                <p className="text-lg text-muted-foreground font-medium">Uploading images...</p>
                <p className="text-base text-muted-foreground/70 mt-2">
                  Please wait while your images are being uploaded
                </p>
              </div>
            ) : images.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                {images.map((image, index) => (
                  <div
                    key={image.id}
                    className="group relative rounded-2xl overflow-hidden bg-muted/50 border border-border/50 hover:border-border transition-all hover:shadow-xl"
                  >
                    <div className="aspect-[4/3]">
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    {/* Index Badge */}
                    <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-sm font-semibold">
                      {index + 1}
                    </div>
                    {/* Delete Button */}
                    <button
                      onClick={() => setConfirmDelete({ type: 'single', storagePath: image.storagePath })}
                      className="absolute top-3 right-3 p-2.5 rounded-xl bg-black/60 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                    {/* Name */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-base text-white truncate font-medium">
                        {image.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center py-16">
                <div className="p-5 rounded-full bg-muted mb-5">
                  <ImageIcon className="h-10 w-10 text-muted-foreground" />
                </div>
                <p className="text-lg text-muted-foreground font-medium">No images yet</p>
                <p className="text-base text-muted-foreground/70 mt-2">
                  Upload images to display in your slideshow
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-5 border-t border-border/50 bg-muted/30">
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setConfirmDelete({ type: 'all' })}
              disabled={uploading || images.length === 0}
            >
              <Trash2 className="h-5 w-5 mr-2" />
              Clear All
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Done
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
